'use client';

import { createContext, useContext, useEffect, type ReactNode } from 'react';

import { setUiTracker } from '@naumu/ui/lib/ui-analytics';

import type { AnalyticsClient } from './types';
import { setClientAnalyticsClient } from './client-analytics';

const AnalyticsContext = createContext<AnalyticsClient | null>(null);

interface AnalyticsProviderProps {
	client: AnalyticsClient;
	children: ReactNode;
}

export const AnalyticsProvider = ({ client, children }: AnalyticsProviderProps) => {
	// Register the client singleton so non-component code can access it
	setClientAnalyticsClient(client);
	// Same registration for the @naumu/ui analytics seam: DS components call
	// `uiTrack` instead of importing the app's analytics client.
	// The DS seam is untyped by design (it must not import the event registry);
	// the cast lands it back on the typed client at the single registration point.
	setUiTracker((event, properties) =>
		client.track(event as Parameters<AnalyticsClient['track']>[0], properties as never),
	);

	// Deferred to idle: the client's init pulls in the analytics vendor bundle
	// and starts its network work, and doing that during hydration competed
	// with the page's own first interaction budget. Everything captured in the
	// meantime is queued by the client and replayed on arrival, so no event -
	// Tier-1 included - is lost by waiting.
	useEffect(() => {
		const start = () => client.init();

		if (typeof window === 'undefined') return;

		// 1s cap on both paths: a visitor who bounces before init never gets a
		// $pageview, and a 3s idle deadline loses most of them. One second is late
		// enough to stay off the hydration critical path, early enough to catch
		// the bounce.
		if (typeof window.requestIdleCallback === 'function') {
			const handle = window.requestIdleCallback(start, { timeout: 1000 });
			return () => window.cancelIdleCallback?.(handle);
		}

		const timer = window.setTimeout(start, 1000);
		return () => window.clearTimeout(timer);
	}, [client]);

	return (
		<AnalyticsContext.Provider value={client}>
			{children}
		</AnalyticsContext.Provider>
	);
};

export const useAnalytics = (): AnalyticsClient => {
	const context = useContext(AnalyticsContext);

	if (!context) {
		throw new Error('useAnalytics must be used within an AnalyticsProvider');
	}

	return context;
};
